//src/store/actions/claimaatachment.reducer.ts
import { createSlice } from '@reduxjs/toolkit'
import {
  fetchAllClaimAttachments,
  addClaimAttachment,
  updateClaimAttachment,
  deleteClaimAttachment,
  fetchAllClaimNotes,
  addClaimNote,
  updateClaimNote,
  deleteClaimNote,
} from '@store/actions/claimattachment.action'
import { ClaimAttachment, Note } from '@dataTypes/claimManagement.types'
import { logger } from '@utils/safeLogger'

type ClaimAttachmentState = {
  attachments: Record<string, ClaimAttachment[]>
  notes: Record<string, Note[]>
  loading: boolean
  error: string | null
}

const initialState: ClaimAttachmentState = {
  attachments: {},
  notes: {},
  loading: false,
  error: null,
}

const claimAttachmentSlice = createSlice({
  name: 'claimAttachment',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllClaimAttachments.fulfilled, (state, action) => {
        const claimId = action.payload.claimId
        if (!claimId) return
        if (!state.attachments[claimId]) {
          state.attachments[claimId] = []
        }
      })

      .addCase(addClaimAttachment.fulfilled, (state, action) => {
        const attachment = action.payload
        if (!attachment.claimId) return

        if (!state.attachments[attachment.claimId]) {
          state.attachments[attachment.claimId] = []
        }

        const existingAttachment = state.attachments[attachment.claimId].find(
          (att) => att.itemNo === attachment.itemNo,
        )

        if (!existingAttachment) {
          state.attachments[attachment.claimId].push(attachment)
        }
      })

      .addCase(updateClaimAttachment.fulfilled, (state, action) => {
        const { claimId, itemNo, updatedAttachment } = action.payload
        if (!claimId || !state.attachments[claimId]) return
        state.attachments[claimId] = state.attachments[claimId].map((att) =>
          att.itemNo === itemNo ? { ...att, ...updatedAttachment } : att,
        )
      })

      .addCase(deleteClaimAttachment.fulfilled, (state, action) => {
        const { claimId, itemNo } = action.payload
        if (!claimId || !state.attachments[claimId]) return
        state.attachments[claimId] = state.attachments[claimId].filter(
          (att) => att.itemNo !== itemNo,
        )
      })

      .addCase(fetchAllClaimNotes.fulfilled, (state, action) => {
        const claimId = action.payload.claimId
        if (!claimId) return
        if (!state.notes[claimId]) {
          state.notes[claimId] = []
        }
      })

      .addCase(addClaimNote.fulfilled, (state, action) => {
        const { claimId, note } = action.payload
        if (!claimId || !note) return

        if (!state.notes[claimId]) {
          state.notes[claimId] = []
        }

        state.notes[claimId].push(note)

        const attachment = state.attachments[claimId]?.find(
          (att) => att.itemNo === note.relatedAttachmentItemNo,
        )
        if (attachment) {
          if (!attachment.notes) {
            attachment.notes = []
          }
          attachment.notes.push(note)
        }
      })

      .addCase(updateClaimNote.fulfilled, (state, action) => {
        const { claimId, itemNo, index, updatedNote } = action.payload

        if (!claimId || !state.attachments[claimId] || index === undefined)
          return

        if (
          state.notes[claimId] &&
          index >= 0 &&
          index < state.notes[claimId].length
        ) {
          state.notes[claimId][index] = {
            ...state.notes[claimId][index],
            ...updatedNote,
          }
        }

        const attachment = state.attachments[claimId]?.find(
          (att) => att.itemNo === itemNo,
        )

        if (attachment) {
          if (!attachment.notes) {
            attachment.notes = []
          }

          if (index >= 0 && index < attachment.notes.length) {
            attachment.notes[index] = {
              ...attachment.notes[index],
              ...updatedNote,
            }
          } else {
            logger.warn('Note index out of bounds')
          }
        }
      })

      .addCase(deleteClaimNote.fulfilled, (state, action) => {
        const { claimId, index } = action.payload
        if (!claimId || !state.notes[claimId]) return

        state.notes[claimId] = state.notes[claimId].filter(
          (_, i) => i !== index,
        )

        state.attachments[claimId]?.forEach((att) => {
          att.notes = att.notes.filter((_, i) => i !== index)
        })
      })
  },
})

export const { clearError } = claimAttachmentSlice.actions
export default claimAttachmentSlice.reducer
